import {
  formatCurrency,
  transformDateToLocalDateString,
} from "helpers";
import { InvoiceIncomingRow } from "services/api/finance/incoming-invoices/types";
import {
  INCOMING_SOURCE_LABELS,
  INVOICE_ALLOCATION_STATUS_LABELS,
} from "services/api/finance/incoming-invoices/consts";
import { InvoiceAllocationStatus } from "services/api/finance/incoming-invoices/types";
import { Badge } from "ui/components/Badge";
import { BadgeProps } from "ui/components/Badge/types";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import Tooltip from "ui/components/Tooltip/Tooltip";
import ContactCell from "ui/components/Table/TableSearch/components/Cells/ContactCell";
import { PictureIcon } from "ui/components/Pictures";

export const INCOMING_INVOICE_SEARCH_COLUMNS = (
  t: (key: string) => string,
  handleApproveClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handleRejectClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handleMarkPaidClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handleDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handleContactClick: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string
  ) => void
): Column[] => {

  const getIncomingStatusBadgeVariant = (statusId: string | null): BadgeProps["variant"] => {
    switch (statusId) {
      case "received":
        return "info";
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      case "paid":
        return "success-dark";
      case "partially_paid":
        return "warning";
      default:
        return "info";
    }
  };

  const getAllocationBadgeVariant = (status: string | null): BadgeProps["variant"] => {
    switch (status) {
      case InvoiceAllocationStatus.FULL:
        return "success";
      case InvoiceAllocationStatus.PARTIAL:
        return "warning";
      default:
        return "danger";
    }
  };

  return [
  {
    title: "",
    key: "invoice_documents",
    field: "",
    alwaysVisible: true,
    type: ColumnTypes.custom,
    // Klik na ikonu otvara dokument računa u novom tabu — isto kao na ostalim pretragama.
    render: (row: InvoiceIncomingRow) => {
      if (!row.documents || row.documents.length === 0) return null;

      return (
        <PictureIcon
          size="sm"
          items={row.documents.map((document) => ({
            id: document.id,
            url: document.url,
            fileName: document.originalFileName,
            tooltip: document.originalFileName,
          }))}
        />
      );
    },
  },
    {
    title: "Broj računa",
    field: "invoiceNumber",
    type: ColumnTypes.string,
  },
  {
    title: "Dobavljač",
    field: "",
    type: ColumnTypes.custom,
    render: (row: InvoiceIncomingRow) => {
      if (row.contactSupplier) {
        return (
          <ContactCell
            contact={row.contactSupplier}
            onClick={(e) => row.contactSupplier?.id && handleContactClick(e, row.contactSupplier.id)}
          />
        );
      }
      return (
        <div className="text-sm font-medium truncate">
          {row.supplierName ?? "-"}
        </div>
      );
    },
  },
  
  {
    title: "Status",
    field: "",
    type: ColumnTypes.custom,
    render: (row: InvoiceIncomingRow) => {
      if (!row.status) return null;
      return (
        <Badge
          text={row.status.name}
          variant={getIncomingStatusBadgeVariant(row.status.id)}
          showDot
        />
      );
    },
  },
  {
    title: "Izvor",
    field: "",
    type: ColumnTypes.custom,
    render: (row: InvoiceIncomingRow) => {
      if (!row.incomingSourceId) return <div className="text-sm text-slate-400">-</div>;
      const sourceName = INCOMING_SOURCE_LABELS[row.incomingSourceId] ?? row.incomingSourceId;
      return <Badge text={sourceName} variant="info" />;
    },
  },
  {
    title: "Raspodjela",
    field: "",
    type: ColumnTypes.custom,
    // Račun bez stavki nema što rasporediti, pa mu ni oznaka ne treba.
    render: (row: InvoiceIncomingRow) => {
      if (row.itemCount === 0) return <div className="text-sm text-slate-400">-</div>;

      const label = row.allocationStatus === InvoiceAllocationStatus.PARTIAL
        ? `Djelomično ${row.allocatedItemCount}/${row.itemCount}`
        : INVOICE_ALLOCATION_STATUS_LABELS[row.allocationStatus ?? InvoiceAllocationStatus.NONE];

      return (
        <Badge
          text={label}
          variant={getAllocationBadgeVariant(row.allocationStatus)}
          height="small"
          showDot
        />
      );
    },
  },
  {
    title: "Datum računa",
    field: "",
    type: ColumnTypes.custom,
    render: (row: InvoiceIncomingRow) => {
      return (
        <div>
          {transformDateToLocalDateString(row.invoiceDate)}
        </div>
      );
    },
  },
  {
    title: "Dospijeće",
    field: "",
    type: ColumnTypes.custom,
    render: (row: InvoiceIncomingRow) => {
      return (
        <div>
          {transformDateToLocalDateString(row.dueDate)}
        </div>
      );
    },
  },
  {
    title: "Iznos",
    field: "",
    type: ColumnTypes.custom,
    render: (row: InvoiceIncomingRow) => {
      return (
        <Tooltip
          decorate={<span className="text-md font-medium">{formatCurrency.format(row.total ?? 0)}</span>}
          position="bottom-right"
          size="medium"
        >
          <div className="min-w-[240px]">
            <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Pregled iznosa</div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Cijena bez PDV-a</span>
              <span className="font-medium">{formatCurrency.format(row.totalWithoutTax ?? 0)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">PDV</span>
              <span className="font-medium">{formatCurrency.format(row.totalTax ?? 0)}</span>
            </div>
            <div className="border-t border-slate-600 my-2"></div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">Ukupno s PDV-om</span>
              <span className="font-bold text-green-400">{formatCurrency.format(row.total ?? 0)}</span>
            </div>
            {(row.totalPaid ?? 0) > 0 && (
              <>
                <div className="border-t border-slate-600 my-2"></div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-green-400">Uplaćeno</span>
                  <span className="font-medium text-green-400">{formatCurrency.format(row.totalPaid ?? 0)}</span>
                </div>
                {(row.remainingAmount ?? 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-400">Preostalo</span>
                    <span className="font-medium text-amber-400">{formatCurrency.format(row.remainingAmount ?? 0)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </Tooltip>
      );
    },
  },
  {
    title: "",
    field: "",
    type: ColumnTypes.custom,
    render: (row: InvoiceIncomingRow) => {
      return (
        <ButtonMenu
          endIcon="/media/icons/more-dots.svg"
          cssClassName="h-full"
          variant="text"
        >
          <ButtonMenuItem
            startIcon="/media/icons/check.svg"
            onClick={(e) => handleApproveClick(e, row.id)}
          >
            Odobri
          </ButtonMenuItem>
          <ButtonMenuItem
            startIcon="/media/icons/close.svg"
            onClick={(e) => handleRejectClick(e, row.id)}
          >
            Odbij
          </ButtonMenuItem>
          <ButtonMenuItem
            startIcon="/media/icons/check.svg"
            onClick={(e) => handleMarkPaidClick(e, row.id)}
          >
            Označi plaćenim
          </ButtonMenuItem>
          <ButtonMenuItem
            startIcon="/media/icons/trash.svg"
            variant="remove"
            onClick={(e) => handleDeleteClick(e, row.id)}
          >
            Obriši
          </ButtonMenuItem>
        </ButtonMenu>
      );
    },
  },
]};



export const INCOMING_INVOICE_ITEM_CHILD_COLUMNS = (): Column[] => [
  { title: "Šifra", field: "", type: ColumnTypes.string },
  { title: "Naziv", field: "", type: ColumnTypes.string },
  { title: "Količina", field: "", type: ColumnTypes.string },
  { title: "Cijena", field: "", type: ColumnTypes.string },
  { title: "PDV", field: "", type: ColumnTypes.string },
  { title: "Bez PDV-a", field: "", type: ColumnTypes.string },
  { title: "Ukupno", field: "", type: ColumnTypes.string },
  { title: "Raspodjela", field: "", type: ColumnTypes.string },
];
